import { useState, useEffect } from 'react';
import { useLanguage } from '@/lib/i18n';
import { useToast } from '@/hooks/use-toast';
import { fetchAllJobs, createJob, updateJob, deleteJob, Job } from '@/lib/jobs';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Edit2, Plus } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

interface JobFormData {
  title: string;
  location: string;
  employment_type: 'full_time' | 'part_time' | 'contract';
  description: string;
  requirements: string;
  salary_min?: number;
  salary_max?: number;
  lang: 'de' | 'bs' | 'en';
  published: boolean;
}

const JobManager = () => {
  const { lang } = useLanguage();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    location: '',
    employment_type: 'full_time',
    description: '',
    requirements: '',
    lang: 'de',
    published: false,
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    const data = await fetchAllJobs();
    setJobs(data);
    setLoading(false);
  };

  const handleReset = () => {
    setFormData({
      title: '',
      location: '',
      employment_type: 'full_time',
      description: '',
      requirements: '',
      lang: 'de',
      published: false,
    });
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.location || !formData.description) {
      toast({
        title: 'Fehler',
        description: 'Bitte füllen Sie alle erforderlichen Felder aus.',
        variant: 'destructive',
      });
      return;
    }

    if (editingId) {
      const success = await updateJob(editingId, formData);
      if (success) {
        toast({
          title: 'Erfolg',
          description: 'Job aktualisiert.',
        });
        await loadJobs();
        handleReset();
        setShowForm(false);
      } else {
        toast({
          title: 'Fehler',
          description: 'Job konnte nicht aktualisiert werden.',
          variant: 'destructive',
        });
      }
    } else {
      const newJob = await createJob(formData);
      if (newJob) {
        toast({
          title: 'Erfolg',
          description: 'Job erstellt.',
        });
        await loadJobs();
        handleReset();
        setShowForm(false);
      } else {
        toast({
          title: 'Fehler',
          description: 'Job konnte nicht erstellt werden.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleEdit = (job: Job) => {
    setFormData({
      title: job.title,
      location: job.location,
      employment_type: job.employment_type,
      description: job.description,
      requirements: job.requirements,
      salary_min: job.salary_min,
      salary_max: job.salary_max,
      lang: job.lang,
      published: job.published,
    });
    setEditingId(job.id);
    setShowForm(true);
  };

  const handleDelete = async (jobId: string) => {
    if (confirm('Wirklich löschen?')) {
      const success = await deleteJob(jobId);
      if (success) {
        toast({
          title: 'Erfolg',
          description: 'Job gelöscht.',
        });
        await loadJobs();
      } else {
        toast({
          title: 'Fehler',
          description: 'Job konnte nicht gelöscht werden.',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Job Management</h2>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus size={18} />
            Neuer Job
          </Button>
        )}
      </div>

      {/* Job Form */}
      {showForm && (
        <Card className="bg-muted/50">
          <CardHeader>
            <CardTitle>{editingId ? 'Job bearbeiten' : 'Neuer Job'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Titel *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="location">Standort *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="employmentType">Beschäftigungstyp *</Label>
                  <Select value={formData.employment_type} onValueChange={(value: any) => setFormData({ ...formData, employment_type: value })}>
                    <SelectTrigger id="employmentType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_time">Vollzeit</SelectItem>
                      <SelectItem value="part_time">Teilzeit</SelectItem>
                      <SelectItem value="contract">Befristet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="lang">Sprache *</Label>
                  <Select value={formData.lang} onValueChange={(value: any) => setFormData({ ...formData, lang: value })}>
                    <SelectTrigger id="lang">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="de">Deutsch</SelectItem>
                      <SelectItem value="bs">Bosnisch</SelectItem>
                      <SelectItem value="en">Englisch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Beschreibung *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={5}
                  required
                />
              </div>

              <div>
                <Label htmlFor="requirements">Anforderungen</Label>
                <Textarea
                  id="requirements"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="salaryMin">Mindestgehalt (€)</Label>
                  <Input
                    id="salaryMin"
                    type="number"
                    value={formData.salary_min || ''}
                    onChange={(e) => setFormData({ ...formData, salary_min: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
                <div>
                  <Label htmlFor="salaryMax">Maximalgehalt (€)</Label>
                  <Input
                    id="salaryMax"
                    type="number"
                    value={formData.salary_max || ''}
                    onChange={(e) => setFormData({ ...formData, salary_max: e.target.value ? Number(e.target.value) : undefined })}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="published"
                  checked={formData.published}
                  onCheckedChange={(checked) => setFormData({ ...formData, published: checked as boolean })}
                />
                <Label htmlFor="published" className="cursor-pointer">
                  Veröffentlicht
                </Label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    handleReset();
                    setShowForm(false);
                  }}
                >
                  Abbrechen
                </Button>
                <Button type="submit">{editingId ? 'Aktualisieren' : 'Erstellen'}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Jobs List */}
      <div className="space-y-4">
        {loading ? (
          <p className="text-muted-foreground">Lädt...</p>
        ) : jobs.length === 0 ? (
          <p className="text-muted-foreground">Keine Jobs verfügbar.</p>
        ) : (
          jobs.map((job) => (
            <Card key={job.id}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <CardTitle>{job.title}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {job.location} • {job.employment_type === 'full_time' ? 'Vollzeit' : job.employment_type === 'part_time' ? 'Teilzeit' : 'Befristet'} • {job.lang.toUpperCase()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(job)}
                      className="gap-2"
                    >
                      <Edit2 size={16} />
                      Bearbeiten
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(job.id)}
                      className="gap-2"
                    >
                      <Trash2 size={16} />
                      Löschen
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="font-semibold">Beschreibung:</p>
                    <p className="whitespace-pre-wrap text-muted-foreground">{job.description}</p>
                  </div>
                  {job.requirements && (
                    <div>
                      <p className="font-semibold">Anforderungen:</p>
                      <p className="whitespace-pre-wrap text-muted-foreground">{job.requirements}</p>
                    </div>
                  )}
                  {job.salary_min && job.salary_max && (
                    <div>
                      <p className="font-semibold">
                        Gehalt: €{job.salary_min.toLocaleString()} - €{job.salary_max.toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold">
                      Status: {job.published ? '✓ Veröffentlicht' : '✗ Entwurf'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default JobManager;
